// src/App.js
// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";

import { ErrorBoundary } from "./components/ErrorBoundary";
import Header from "./components/Header";
import Profile from "./components/Profile";
import ConnectorsPage from "./components/ConnectorsPage";
import Dashboard from "./components/Dashboard";
import MissionExecutionCenter from "./components/MissionExecutionCenter";
import MissionLaunchPage from "./components/mission-launch/MissionLaunchPage";
import ExecutiveIntelligencePage from "./components/ExecutiveIntelligencePage";
import Compliance from "./components/Compliance";
import AlertsPage from "./components/AlertsPage";
import DriftStatePage from "./components/DriftStatePage";
import EvidenceAuditPage from "./components/EvidenceAuditPage";
import Library from "./components/Library";
import SOPCenterPage from "./components/SOPCenterPage";
import WorkflowCenter from "./components/WorkflowCenter";
import ValidationDashboardPage from "./components/ValidationDashboardPage";
import EnterpriseAdminPage from "./components/enterprise-admin/EnterpriseAdminPage";
import CustomerEntryGate from "./components/customerLifecycle/CustomerEntryGate";
import TrustCenterPage from "./components/trust/TrustCenterPage";
import LegalDocumentPage from "./components/legal/LegalDocumentPage";
import PaymentReturnPage, { parsePaymentReturnHash } from "./components/payments/PaymentReturnPage";
import InvoicePaymentPage, { parseInvoicePaymentPath } from "./components/payments/InvoicePaymentPage";

import { AdvisorProvider } from "./AdvisorContext";
import AdvisorLauncher from "./components/AdvisorLauncher";
import {
  applyOasysThemePreference,
  getOasysEffectiveTheme,
  getOasysThemePreference,
} from "./theme";

import "./styles/variables.css";
import "./App.css";
import "./styles/SettingsShellV2.css";

// ────────────────────────────────────────────────────────────
// Env & URLs
// ────────────────────────────────────────────────────────────
const CONFIGURED_API_BASE = String(process.env.REACT_APP_API_BASE_URL || "").trim();
const DEVELOPMENT_API_BASE = "http://localhost:8000";
const RAW_BASE =
  CONFIGURED_API_BASE ||
  (process.env.NODE_ENV === "development" ? DEVELOPMENT_API_BASE : "");

if (!RAW_BASE) {
  throw new Error(
    "Missing REACT_APP_API_BASE_URL. Production OASYS builds require an explicit API base URL."
  );
}

const TRIMMED = RAW_BASE.replace(/\/+$/, "");
const API_BASE = TRIMMED.toLowerCase().endsWith("/v1") ? TRIMMED.slice(0, -3) : TRIMMED;
const V1_BASE = `${API_BASE}/v1`;

if (typeof window !== "undefined") {
  window.__API_BASE = API_BASE;
  window.__V1_BASE = V1_BASE;
}

const AUTH0_AUDIENCE = process.env.REACT_APP_AUTH0_AUDIENCE;

axios.defaults.baseURL = API_BASE;
axios.defaults.withCredentials = true;

// ────────────────────────────────────────────────────────────
// Route helpers
// ────────────────────────────────────────────────────────────
function normalizeMode(mode) {
  const value = String(mode || "").trim().toLowerCase();

  if (!value || value === "dashboard" || value === "mission-control") {
    return "dashboard";
  }

  if (
    value === "mission-launch" ||
    value === "mission_launch" ||
    value === "launch" ||
    value === "mission-onboarding" ||
    value === "mission_onboarding" ||
    value === "onboarding"
  ) {
    return "mission-launch";
  }

  if (
    value === "execution" ||
    value === "mission-execution" ||
    value === "mission_execution" ||
    value === "mission-execution-center" ||
    value === "mission_execution_center" ||
    value === "execution-center" ||
    value === "execution_center" ||
    value === "work-queue" ||
    value === "work_queue" ||
    value === "queue"
  ) {
    return "execution";
  }

  if (
    value === "workflow" ||
    value === "workflows" ||
    value === "workflow-center" ||
    value === "workflow_center"
  ) {
    return "workflows";
  }

  if (
    value === "executive" ||
    value === "executive-intelligence" ||
    value === "executive_intelligence" ||
    value === "exec"
  ) {
    return "executive";
  }

  if (
    value === "validation" ||
    value === "validation-dashboard" ||
    value === "validation_dashboard" ||
    value === "validation-intelligence" ||
    value === "validation_intelligence"
  ) {
    return "validation";
  }

  if (value === "posture") return "posture";
  if (value === "drift" || value === "drift-state" || value === "driftandstate") {
    return "drift";
  }
  if (value === "alerts") return "alerts";
  if (
    value === "evidence" ||
    value === "evidence-audit" ||
    value === "evidence_audit" ||
    value === "audit"
  ) {
    return "evidence";
  }
  if (value === "vendors") return "vendors";
  if (value === "sop" || value === "sop-center" || value === "sopcenter") {
    return "sop";
  }
  if (value === "automations") return "automations";
  if (value === "marketplace") return "marketplace";
  if (value === "settings") return "settings";
  if (
    value === "administration" ||
    value === "admin" ||
    value === "enterprise-admin" ||
    value === "enterprise_admin" ||
    value === "enterprise-administration" ||
    value === "enterprise_administration" ||
    value === "access-control" ||
    value === "access_control"
  ) {
    return "administration";
  }

  if (value === "library") return "library";
  if (
    value === "trust" ||
    value === "trust-center" ||
    value === "trust_center"
  ) {
    return "trust";
  }
  if (
    value === "lifecycle" ||
    value === "data-lifecycle" ||
    value === "data_lifecycle" ||
    value === "data-lifecycle-policy" ||
    value === "data_lifecycle_policy"
  ) {
    return "lifecycle";
  }
  if (
    value === "privacy" ||
    value === "privacy-policy" ||
    value === "privacy_policy"
  ) {
    return "privacy";
  }
  if (
    value === "terms" ||
    value === "customer-terms" ||
    value === "customer_terms" ||
    value === "terms-of-service" ||
    value === "terms_of_service"
  ) {
    return "terms";
  }
  if (value === "compliance") return "compliance";
  if (value === "profile") return "profile";
  if (value === "subscribe" || value === "billing") return "settings";
  if (value === "connectors") return "connectors";

  return value;
}

function parseHash() {
  const raw = typeof window !== "undefined" ? window.location.hash : "";
  const trimmed = (raw || "").replace(/^#/, "");
  const [path, query = ""] = trimmed.split("?");
  const rawMode = String(path || "dashboard").trim().toLowerCase();
  const params = Object.fromEntries(new URLSearchParams(query).entries());
  const isLegacyConnectorRoute = rawMode === "connectors";

  if (isLegacyConnectorRoute && !params.section) {
    params.section = "connectors";
  }

  return {
    mode: isLegacyConnectorRoute ? "administration" : normalizeMode(rawMode),
    params,
    isLegacyConnectorRoute,
  };
}

function initialRoute() {
  if (typeof window === "undefined") {
    return { mode: "dashboard", params: {}, isLegacyConnectorRoute: false };
  }

  return parseHash();
}


function routeFlagEnabled(value) {
  return ["1", "true", "yes", "on"].includes(
    String(value || "").trim().toLowerCase()
  );
}

function missionLaunchReviewRequested(mode, params = {}) {
  return (
    normalizeMode(mode) === "mission-launch" &&
    (
      routeFlagEnabled(params.review) ||
      routeFlagEnabled(params.reviewLaunch) ||
      routeFlagEnabled(params.review_launch)
    )
  );
}

function missionLaunchGateApplies(mode) {
  const normalized = normalizeMode(mode);
  return normalized === "dashboard" || normalized === "mission-launch";
}

function normalizeMissionLaunchStep(value) {
  const step = String(value || "").trim().toLowerCase();
  const allowed = new Set([
    "welcome",
    "organization",
    "frameworks",
    "connectors",
    "scan",
    "brief",
    "readiness",
    "ready",
  ]);

  return allowed.has(step) ? step : "welcome";
}

function replaceLegacyConnectorHash(params = {}) {
  if (typeof window === "undefined") return;

  const nextParams = {
    ...params,
    section: params.section || "connectors",
  };
  const query = new URLSearchParams(nextParams).toString();
  const nextHash = `#administration${query ? `?${query}` : ""}`;

  if (window.location.hash === nextHash) return;

  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${window.location.search}${nextHash}`
  );
}

function scrollAppToTop(behavior = "auto") {
  if (typeof window === "undefined") return;

  try {
    window.scrollTo({ top: 0, left: 0, behavior });
  } catch {
    window.scrollTo(0, 0);
  }

  const scrollTargets = [
    document.scrollingElement,
    document.documentElement,
    document.body,
    document.querySelector("#root"),
    document.querySelector(".App"),
    document.querySelector("main"),
  ];

  scrollTargets.forEach((target) => {
    if (!target) return;
    try {
      target.scrollTop = 0;
      target.scrollLeft = 0;
    } catch {
      // Ignore non-scrollable nodes.
    }
  });
}

function scheduleScrollAppToTop() {
  scrollAppToTop("auto");

  if (typeof window === "undefined") return;

  window.requestAnimationFrame(() => {
    scrollAppToTop("auto");
    window.requestAnimationFrame(() => {
      scrollAppToTop("auto");
      window.setTimeout(() => scrollAppToTop("auto"), 0);
      window.setTimeout(() => scrollAppToTop("auto"), 80);
      window.setTimeout(() => scrollAppToTop("auto"), 180);
    });
  });
}

function PlaceholderModule({ title, description }) {
  return (
    <div className="placeholder">
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
  );
}

function normalizeSettingsSection(section) {
  const value = String(section || "").trim().toLowerCase();
  return ["connectors", "profile", "workspace", "access"].includes(value)
    ? value
    : "connectors";
}

function SettingsAdminShell({
  apiBaseUrl,
  tenantId,
  userId,
  userProfile,
  getAccessToken,
  routeParams = {},
  onModeChange,
  initialSection = "connectors",
}) {
  const [activeSection, setActiveSection] = useState(
    normalizeSettingsSection(initialSection)
  );

  useEffect(() => {
    setActiveSection(normalizeSettingsSection(initialSection));
  }, [initialSection]);

  const sections = [
    {
      key: "connectors",
      label: "Connectors",
      description: "Integration health, sync recovery, and audit history.",
    },
    {
      key: "profile",
      label: "Profile",
      description: "User profile, company identity, and account metadata.",
    },
    {
      key: "workspace",
      label: "Workspace",
      description: "Workspace configuration and tenant-level preferences.",
    },
    {
      key: "access",
      label: "Access",
      description: "Users, roles, SSO, and administrative access controls.",
    },
  ];

  const activeMeta = sections.find((item) => item.key === activeSection) || sections[0];

  return (
    <div className="settings-shell">
      <section className="settings-hero">
        <div>
          <p className="settings-eyebrow">Admin Settings</p>
          <h2>Settings</h2>
          <p>
            Centralize connector operations, account profile, workspace
            configuration, and access controls.
          </p>
        </div>
      </section>

      <section className="settings-layout">
        <aside className="settings-sidebar" aria-label="Settings sections">
          {sections.map((section) => (
            <button
              key={section.key}
              type="button"
              className={activeSection === section.key ? "active" : ""}
              onClick={() => setActiveSection(section.key)}
            >
              <span>{section.label}</span>
              <small>{section.description}</small>
            </button>
          ))}
        </aside>

        <div className="settings-panel">
          <div className="settings-panel-header">
            <div>
              <h3>{activeMeta.label}</h3>
              <p>{activeMeta.description}</p>
            </div>
            <button type="button" className="settings-back-button" onClick={() => onModeChange("dashboard")}>Back to Mission Control</button>
          </div>

          {activeSection === "connectors" && (
            <ConnectorsPage
              apiBaseUrl={apiBaseUrl}
              tenantId={tenantId}
              userId={userId}
              getAccessToken={getAccessToken}
              routeParams={routeParams}
              onModeChange={onModeChange}
            />
          )}

          {activeSection === "profile" && (
            <Profile apiBaseUrl={apiBaseUrl} tenantId={tenantId} userId={userId} getAccessToken={getAccessToken} />
          )}

          {activeSection === "workspace" && (
            <PlaceholderModule
              title="Workspace"
              description="Workspace-level settings will include company profile, tenant preferences, and operating defaults."
            />
          )}

          {activeSection === "access" && (
            <PlaceholderModule
              title="Access"
              description="Access settings will manage users, roles, SSO, API keys, and administrative permissions."
            />
          )}
        </div>
      </section>
    </div>
  );
}

export default function App() {
  const {
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    getAccessTokenSilently,
    logout,
    user,
  } = useAuth0();

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.__AUD__ = AUTH0_AUDIENCE;
      window.__getApiToken__ = (opts = {}) =>
        getAccessTokenSilently({
          authorizationParams: {
            audience: AUTH0_AUDIENCE,
            ...(opts.authorizationParams || {}),
          },
        });
      window.__loginWithAudience__ = () =>
        loginWithRedirect({ authorizationParams: { audience: AUTH0_AUDIENCE } });

    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const init = initialRoute();
  const [mode, _setMode] = useState(init.mode);
  const profileReturnHash = useRef("#dashboard");
  const [routeParams, setRouteParams] = useState(init.params);
  const [routeRenderKey, setRouteRenderKey] = useState(0);
  const [themePreference, setThemePreference] = useState(getOasysThemePreference());
  const [themeMode, setThemeMode] = useState(getOasysEffectiveTheme());
  const [missionLaunchGateState, setMissionLaunchGateState] = useState("idle");
  const [missionLaunchGateError, setMissionLaunchGateError] = useState("");
  const [missionLaunchGateRetryNonce, setMissionLaunchGateRetryNonce] = useState(0);
  const [customerEntry, setCustomerEntry] = useState(null);
  const [authRedirecting, setAuthRedirecting] = useState(false);
  const paymentReturnRoute =
    typeof window !== "undefined"
      ? parsePaymentReturnHash(window.location.hash)
      : null;

  const invoicePaymentRoute =
    typeof window !== "undefined"
      ? parseInvoicePaymentPath(
          window.location.pathname,
          window.location.hash
        )
      : null;

  useEffect(() => {
    if (init.isLegacyConnectorRoute) {
      replaceLegacyConnectorHash(init.params);
    }
  }, [init.isLegacyConnectorRoute, init.params]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const previousScrollRestoration = window.history.scrollRestoration;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    return () => {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = previousScrollRestoration;
      }
    };
  }, []);

  const handleThemePreferenceChange = useCallback((nextPreference) => {
    const applied = applyOasysThemePreference(nextPreference, { persist: true });
    setThemePreference(applied.preference);
    setThemeMode(applied.effectiveTheme);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia
      ? window.matchMedia("(prefers-color-scheme: dark)")
      : null;

    const handleSystemThemeChange = () => {
      const preference = getOasysThemePreference();
      if (preference !== "system") return;

      const applied = applyOasysThemePreference("system", { persist: false });
      setThemePreference(applied.preference);
      setThemeMode(applied.effectiveTheme);
    };

    if (mq?.addEventListener) mq.addEventListener("change", handleSystemThemeChange);
    else if (mq?.addListener) mq.addListener(handleSystemThemeChange);

    return () => {
      if (mq?.removeEventListener) mq.removeEventListener("change", handleSystemThemeChange);
      else if (mq?.removeListener) mq?.removeListener(handleSystemThemeChange);
    };
  }, []);

  useEffect(() => {
    const syncStoredTheme = () => {
      const preference = getOasysThemePreference();
      const applied = applyOasysThemePreference(preference, { persist: false });
      setThemePreference(applied.preference);
      setThemeMode(applied.effectiveTheme);
    };

    window.addEventListener("storage", syncStoredTheme);
    window.addEventListener("oasys:theme-change", syncStoredTheme);

    return () => {
      window.removeEventListener("storage", syncStoredTheme);
      window.removeEventListener("oasys:theme-change", syncStoredTheme);
    };
  }, []);

  const setMode = useCallback((next, params) => {
    let nextMode = typeof next === "string" ? next : String(next || "");
    let nextParams = {};

    if (nextMode.includes("?")) {
      const [m, q] = nextMode.split("?");
      nextMode = m;
      nextParams = Object.fromEntries(new URLSearchParams(q));
    }

    nextMode = normalizeMode(nextMode);

    if ((nextMode === "profile" || nextMode === "settings") &&
        !["profile", "settings"].includes(parseHash().mode)) {
      profileReturnHash.current = window.location.hash || "#dashboard";
    }

    if (params && typeof params === "object") {
      nextParams = { ...nextParams, ...params };
    }

    if (nextMode === "connectors") {
      nextMode = "administration";
      nextParams = {
        ...nextParams,
        section: nextParams.section || "connectors",
      };
    }

    if (nextMode === "dashboard") {
      nextParams = {
        ...nextParams,
        routeNavNonce: String(Date.now()),
        scrollTop: "1",
      };
      scheduleScrollAppToTop();
    }

    _setMode(nextMode);
    setRouteParams(nextParams);
    setRouteRenderKey((value) => value + 1);

    const qs = new URLSearchParams(nextParams).toString();
    const newHash = `#${nextMode}${qs ? `?${qs}` : ""}`;
    if (window.location.hash !== newHash) window.location.hash = newHash;

    if (nextMode === "dashboard") {
      scheduleScrollAppToTop();
    }
  }, []);

  const exitPersonalProfile = useCallback(() => {
    const previous = profileReturnHash.current;
    const saved = previous && !["profile", "settings"].includes(normalizeMode(previous.slice(1).split("?")[0]))
      ? previous : "#dashboard";
    const parsed = saved.replace(/^#/, "");
    const [target, query = ""] = parsed.split("?");
    setMode(target, Object.fromEntries(new URLSearchParams(query)));
  }, [setMode]);

  useEffect(() => {
    const onHashChange = () => {
      const {
        mode: nextMode,
        params,
        isLegacyConnectorRoute,
      } = parseHash();

      if (isLegacyConnectorRoute) {
        replaceLegacyConnectorHash(params);
      }

      if (nextMode === "dashboard") {
        scheduleScrollAppToTop();
      }

      _setMode(nextMode);
      setRouteParams(params);
      setRouteRenderKey((value) => value + 1);

      if (nextMode === "dashboard") {
        scheduleScrollAppToTop();
      }
    };

    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    document.body.dataset.route = mode;
    return () => {
      delete document.body.dataset.route;
    };
  }, [mode]);

  useEffect(() => {
    const isPublicLegalRoute = mode === "privacy" || mode === "terms" || mode === "lifecycle";
    const isPublicPaymentReturnRoute = Boolean(paymentReturnRoute);
    const isPublicInvoicePaymentRoute = Boolean(invoicePaymentRoute);

    if (
      !isPublicLegalRoute
      && !isPublicPaymentReturnRoute
      && !isPublicInvoicePaymentRoute
      && !isLoading
      && !isAuthenticated
    ) {
      loginWithRedirect({
        authorizationParams: AUTH0_AUDIENCE ? { audience: AUTH0_AUDIENCE } : undefined,
      });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect, mode, paymentReturnRoute, invoicePaymentRoute]);

  const getApiAccessToken = useCallback(async () => {
    if (!AUTH0_AUDIENCE) {
      throw new Error("Missing REACT_APP_AUTH0_AUDIENCE env var");
    }
    try {
      return await getAccessTokenSilently({
        authorizationParams: { audience: AUTH0_AUDIENCE },
      });
    } catch (err) {
      const msg = String(err?.error || err?.message || err);
      if (
        msg.includes("login_required") ||
        msg.includes("consent_required") ||
        msg.includes("missing_refresh_token")
      ) {
        setAuthRedirecting(true);
        try {
          await loginWithRedirect({
            authorizationParams: { audience: AUTH0_AUDIENCE },
          });
        } catch (redirectError) {
          setAuthRedirecting(false);
          throw redirectError;
        }
        const transitionError = new Error("Authentication redirect in progress.");
        transitionError.name = "AuthRedirectInProgress";
        throw transitionError;
      }
      throw err;
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const [userProfile, setUserProfile] = useState(undefined);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [profileError, setProfileError] = useState(null);

  const lifecycleResolved = customerEntry?.entry_state === "existing_member";
  const tenantId = lifecycleResolved ? String(customerEntry?.tenant_id || "").trim() : "";
  const userId = lifecycleResolved ? String(customerEntry?.user_id || "").trim() : "";

  const handleCustomerEntryResolved = useCallback((entry) => {
    if (entry?.entry_state !== "existing_member") return;

    setCustomerEntry(entry);
    setMissionLaunchGateState("idle");
    setMissionLaunchGateError("");
  }, []);

  useEffect(() => {
    setCustomerEntry(null);
    setUserProfile(undefined);
    setProfileError(null);
    setMissionLaunchGateState("idle");
    setMissionLaunchGateError("");
  }, [user?.sub]);

  const fetchProfile = useCallback(async () => {
    setProfileError(null);
    setLoadingProfile(true);
    try {
      const token = await getApiAccessToken();
      if (!token) return;

      const resp = await axios.get(`${V1_BASE}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserProfile(resp.data);
    } catch (err) {
      console.error("Error fetching profile:", err);
      setUserProfile({ app_metadata: {} });
      setProfileError(String(err?.message || err));
    } finally {
      setLoadingProfile(false);
    }
  }, [getApiAccessToken]);

  useEffect(() => {
    if (isAuthenticated && lifecycleResolved && userProfile === undefined) {
      fetchProfile();
    }
  }, [isAuthenticated, lifecycleResolved, userProfile, fetchProfile]);

  const launchReviewRequested = missionLaunchReviewRequested(mode, routeParams);
  const launchGateApplies = missionLaunchGateApplies(mode);

  useEffect(() => {
    if (
      isLoading ||
      !isAuthenticated ||
      !lifecycleResolved ||
      userProfile === undefined ||
      !tenantId ||
      !userId
    ) {
      return undefined;
    }

    if (!launchGateApplies || launchReviewRequested) {
      setMissionLaunchGateState("resolved");
      setMissionLaunchGateError("");
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    const resolveMissionLaunchEntry = async () => {
      setMissionLaunchGateState("loading");
      setMissionLaunchGateError("");

      try {
        const token = await getApiAccessToken();
        if (!token || !active) return;

        const response = await fetch(`${V1_BASE}/mission-launch/status`, {
          method: "GET",
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-Id": tenantId,
            "X-User-Id": userId,
            "X-User-Name":
              userProfile?.name ||
              userProfile?.email ||
              user?.name ||
              user?.email ||
              "OASYS Administrator",
          },
        });

        const responseText = await response.text();
        let payload = {};

        if (responseText) {
          try {
            payload = JSON.parse(responseText);
          } catch {
            payload = { detail: responseText };
          }
        }

        if (!response.ok) {
          const detail = payload?.detail;
          const message =
            typeof detail === "string"
              ? detail
              : detail?.message
              ? detail.message
              : detail
              ? JSON.stringify(detail)
              : `Mission Launch status request failed (${response.status})`;

          throw new Error(message);
        }

        if (!active) return;

        const missionStatus = String(
          payload?.mission_status ||
          payload?.session?.status ||
          "not_started"
        ).trim().toLowerCase();

        if (["completed", "complete", "operational"].includes(missionStatus)) {
          if (mode !== "dashboard") {
            setMode("dashboard", {
              launchRedirect: "completed",
            });
          }
        } else {
          const persistedStep = normalizeMissionLaunchStep(
            payload?.current_phase ||
            payload?.session?.current_step ||
            payload?.next_allowed_step
          );

          if (
            mode !== "mission-launch" ||
            routeParams?.step !== persistedStep
          ) {
            setMode("mission-launch", {
              step: persistedStep,
              resume: "1",
            });
          }
        }

        setMissionLaunchGateState("resolved");
      } catch (error) {
        if (!active || error?.name === "AbortError") return;

        console.error("Mission Launch entry resolution failed:", error);
        setMissionLaunchGateError(error?.message || String(error));
        setMissionLaunchGateState("error");
      }
    };

    resolveMissionLaunchEntry();

    return () => {
      active = false;
      controller.abort();
    };
  }, [
    getApiAccessToken,
    isAuthenticated,
    isLoading,
    launchGateApplies,
    launchReviewRequested,
    missionLaunchGateRetryNonce,
    mode,
    lifecycleResolved,
    routeParams?.step,
    setMode,
    tenantId,
    user?.email,
    user?.name,
    userId,
    userProfile,
  ]);

  useLayoutEffect(() => {
    scheduleScrollAppToTop();
  }, [mode, routeParams]);

  if (invoicePaymentRoute) {
    return (
      <ErrorBoundary>
        <InvoicePaymentPage
          token={invoicePaymentRoute.token}
          v1BaseUrl={V1_BASE}
        />
      </ErrorBoundary>
    );
  }

  if (paymentReturnRoute) {
    return (
      <ErrorBoundary>
        <div className="App" data-theme-mode={themeMode}>
          <main>
            <PaymentReturnPage route={paymentReturnRoute} />
          </main>
        </div>
      </ErrorBoundary>
    );
  }

  if (isLoading) return <div className="loading">Loading authentication…</div>;

  if (authRedirecting) {
    return <div className="loading">Redirecting to secure sign in…</div>;
  }

  const isLegalRoute = mode === "privacy" || mode === "terms" || mode === "lifecycle";

  if (isLegalRoute) {
    return (
      <ErrorBoundary>
        <div className="App" data-theme-mode={themeMode}>
          <main>
            <LegalDocumentPage
              documentKey={mode}
              onModeChange={isAuthenticated ? setMode : null}
            />
          </main>
        </div>
      </ErrorBoundary>
    );
  }

  if (!isLoading && !isAuthenticated) return null;

  if (!lifecycleResolved) {
    return (
      <CustomerEntryGate
        apiBaseUrl={V1_BASE}
        getAccessToken={getApiAccessToken}
        onResolved={handleCustomerEntryResolved}
        onLogout={() => logout({ logoutParams: { returnTo: window.location.origin } })}
      />
    );
  }

  if (loadingProfile || userProfile === undefined) {
    return <div className="loading">Loading profile…</div>;
  }
  if (profileError)
    return (
      <div className="error-screen">
        <p>Failed to load profile:</p>
        <pre>{profileError}</pre>
        <button onClick={fetchProfile}>Retry</button>
        <button onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>
          Log out
        </button>
      </div>
    );


  if (
    launchGateApplies &&
    !launchReviewRequested &&
    ["idle", "loading"].includes(missionLaunchGateState)
  ) {
    return <div className="loading">Resolving Mission Launch…</div>;
  }

  if (
    launchGateApplies &&
    !launchReviewRequested &&
    missionLaunchGateState === "error"
  ) {
    return (
      <div className="error-screen">
        <p>Mission Launch status could not be resolved.</p>
        <pre>{missionLaunchGateError}</pre>
        <button
          type="button"
          onClick={() => setMissionLaunchGateRetryNonce((value) => value + 1)}
        >
          Retry
        </button>
        <button
          type="button"
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        >
          Log out
        </button>
      </div>
    );
  }

  const isMissionLaunch = mode === "mission-launch";
  const isExecution = mode === "execution";
  const isExecutive = mode === "executive";
  const isPosture = mode === "posture";
  const isAlerts = mode === "alerts";
  const isDrift = mode === "drift";
  const isEvidence = mode === "evidence";
  const isValidation = mode === "validation";
  const isCompliance = String(mode || "").toLowerCase().startsWith("compliance");

  // OASYS Copilot is available during Mission Launch as well. MissionLaunchPage
  // supplies governed page/provider context so the existing global launcher can
  // guide onboarding without creating a second Copilot surface.
  const showFloatingAdvisor = true;

  const postureUserProfile = {
    ...userProfile,
    tenant_id: tenantId || userProfile?.tenant_id,
    user_id: userId || userProfile?.user_id,
  };

  const renderMainContent = () => {
    if (mode === "mission-launch") {
      return (
        <MissionLaunchPage
          apiBaseUrl={V1_BASE}
          getAccessToken={getApiAccessToken}
          userProfile={postureUserProfile}
          tenantId={tenantId}
          userId={userId}
          routeParams={routeParams}
          setMode={setMode}
          onModeChange={setMode}
          onGoToMissionControl={() => setMode("dashboard")}
          themeMode={themeMode}
        />
      );
    }

    if (mode === "dashboard") {
      const dashboardKey = `dashboard-${routeParams?.routeNavNonce || routeRenderKey}`;

      return (
        <Dashboard
          key={dashboardKey}
          apiBaseUrl={V1_BASE}
          getAccessToken={getApiAccessToken}
          userProfile={postureUserProfile}
          onModeChange={setMode}
          themeMode={themeMode}
          routeParams={routeParams}
        />
      );
    }

    if (mode === "execution") {
      return (
        <MissionExecutionCenter
          apiBaseUrl={V1_BASE}
          getAccessToken={getApiAccessToken}
          userProfile={postureUserProfile}
          routeParams={routeParams}
          setMode={setMode}
          themeMode={themeMode}
        />
      );
    }

    if (mode === "executive") {
      return (
        <ExecutiveIntelligencePage
          apiBaseUrl={V1_BASE}
          getAccessToken={getApiAccessToken}
          userProfile={postureUserProfile}
          routeParams={routeParams}
          setMode={setMode}
          themeMode={themeMode}
        />
      );
    }

    if (mode === "posture" || mode === "compliance") {
      return (
        <Compliance
          apiBaseUrl={V1_BASE}
          getAccessToken={getApiAccessToken}
          tenantId={tenantId}
          userId={userId}
          routeParams={routeParams}
          setMode={setMode}
          themeMode={themeMode}
        />
      );
    }

    if (mode === "alerts") {
      return (
        <AlertsPage
          apiBaseUrl={V1_BASE}
          getAccessToken={getApiAccessToken}
          tenantId={tenantId}
          userId={userId}
          routeParams={routeParams}
          setMode={setMode}
          themeMode={themeMode}
        />
      );
    }

    if (mode === "drift") {
      return (
        <DriftStatePage
          apiBaseUrl={V1_BASE}
          getAccessToken={getApiAccessToken}
          tenantId={tenantId}
          userId={userId}
          routeParams={routeParams}
          setMode={setMode}
        />
      );
    }

    if (mode === "evidence") {
      return (
        <EvidenceAuditPage
          apiBaseUrl={V1_BASE}
          getAccessToken={getApiAccessToken}
          tenantId={tenantId}
          userId={userId}
          routeParams={routeParams}
          setMode={setMode}
          themeMode={themeMode}
        />
      );
    }

    if (mode === "sop") {
      return (
        <SOPCenterPage
          apiBaseUrl={V1_BASE}
          getAccessToken={getApiAccessToken}
          userProfile={postureUserProfile}
          onModeChange={setMode}
          modeParams={routeParams}
          routeParams={routeParams}
          initialFilter={routeParams?.filter || null}
        />
      );
    }

    if (mode === "workflows") {
      return (
        <WorkflowCenter
          apiBaseUrl={V1_BASE}
          getAccessToken={getApiAccessToken}
          userProfile={postureUserProfile}
          routeParams={routeParams}
          setMode={setMode}
          themeMode={themeMode}
        />
      );
    }

    if (mode === "validation") {
      return (
        <ValidationDashboardPage
          apiBaseUrl={V1_BASE}
          getAccessToken={getApiAccessToken}
          userProfile={postureUserProfile}
          routeParams={routeParams}
          setMode={setMode}
          themeMode={themeMode}
        />
      );
    }

    if (mode === "profile") {
      return <div className="oasys-focused-profile-page"><div className="oasys-focused-profile-title"><h1>Profile</h1><button type="button" onClick={exitPersonalProfile} aria-label="Close profile" title="Close profile">×</button></div><Profile apiBaseUrl={V1_BASE} tenantId={tenantId} userId={userId} getAccessToken={getApiAccessToken} /></div>;
    }

    if (mode === "library") {
      return (
        <Library
          apiBaseUrl={V1_BASE}
          getAccessToken={getApiAccessToken}
          userProfile={postureUserProfile}
          standalone
          themeMode={themeMode}
          onModeChange={setMode}
        />
      );
    }

    if (mode === "administration") {
      return (
        <EnterpriseAdminPage
          apiBaseUrl={V1_BASE}
          getAccessToken={getApiAccessToken}
          tenantId={tenantId}
          userId={userId}
          userProfile={postureUserProfile}
          routeParams={routeParams}
          onModeChange={setMode}
          themeMode={themeMode}
        />
      );
    }

    if (mode === "trust") {
      return (
        <TrustCenterPage
          apiBaseUrl={V1_BASE}
          getAccessToken={getApiAccessToken}
          onModeChange={setMode}
        />
      );
    }

    if (mode === "privacy" || mode === "terms" || mode === "lifecycle") {
      return <LegalDocumentPage documentKey={mode} onModeChange={setMode} />;
    }

    if (mode === "vendors") {
      return (
        <PlaceholderModule
          title="Vendors"
          description="This module will manage vendors, supplier oversight, and third-party compliance workflows."
        />
      );
    }

    if (mode === "automations") {
      return (
        <PlaceholderModule
          title="Automations"
          description="This module will manage compliance workflows, automated actions, and response orchestration."
        />
      );
    }

    if (mode === "marketplace") {
      return (
        <PlaceholderModule
          title="Marketplace"
          description="This module will host future integrations, packs, templates, and expansion modules."
        />
      );
    }

    if (mode === "settings" && ["profile", ""].includes(String(routeParams?.settingsSection || routeParams?.section || routeParams?.activeSection || "profile").toLowerCase())) {
      return <div className="oasys-focused-profile-page"><div className="oasys-focused-profile-title"><h1>Profile</h1><button type="button" onClick={exitPersonalProfile} aria-label="Close profile" title="Close profile">×</button></div><Profile apiBaseUrl={V1_BASE} tenantId={tenantId} userId={userId} getAccessToken={getApiAccessToken} /></div>;
    }

    if (mode === "settings") {
      return (
        <SettingsAdminShell
          apiBaseUrl={V1_BASE}
          tenantId={tenantId}
          userId={userId}
          userProfile={postureUserProfile}
          getAccessToken={getApiAccessToken}
          routeParams={routeParams}
          onModeChange={setMode}
          initialSection={
            routeParams?.settingsSection ||
            routeParams?.section ||
            routeParams?.activeSection ||
            routeParams?.returnToSettingsSection ||
            "connectors"
          }
        />
      );
    }

    return (
      <div className="placeholder">
        <h2>{mode.charAt(0).toUpperCase() + mode.slice(1)} (Coming Soon)</h2>
        <p>This section is under construction.</p>
      </div>
    );
  };

  const advisorMode = isMissionLaunch
    ? "mission-launch"
    : isExecution
    ? "execution"
    : isPosture || isCompliance
    ? "compliance"
    : isAlerts
    ? "alerts"
    : isDrift
    ? "drift"
    : isEvidence
    ? "evidence"
    : isValidation
    ? "validation"
    : isExecutive
    ? "executive"
    : mode === "administration"
    ? "administration"
    : mode;

  return (
    <ErrorBoundary>
      <AdvisorProvider
        apiBaseUrl={V1_BASE}
        getAccessToken={getApiAccessToken}
        onNavigate={setMode}
        userProfile={userProfile}
        tenantId={tenantId}
        userId={userId}
        persistScope="session"
      >
        <div className="App" data-theme-mode={themeMode}>
          {!isMissionLaunch && (
            <Header
              onModeChange={setMode}
              currentMode={mode}
              themeMode={themeMode}
              themePreference={themePreference}
              onThemePreferenceChange={handleThemePreferenceChange}
              apiBaseUrl={V1_BASE}
              tenantId={tenantId}
              userId={userId}
              getAccessToken={getApiAccessToken}
            />
          )}

          <main>{renderMainContent()}</main>

          {showFloatingAdvisor && (
            <AdvisorLauncher
              mode={advisorMode}
              themeMode={themeMode}
              rightOffset={24}
              bottomOffset={24}
              draggable
              defaultOpen={false}
              sidebarLauncher={!isMissionLaunch}
            />
          )}
        </div>
      </AdvisorProvider>
    </ErrorBoundary>
  );
}
